import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import API, { initializeAuth } from "../../services/api.ts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ViewerNavbar from "./ViewerNavbar";
import "./SignalViewerPage.css";

// Lazy-load SignalChart so the page renders even if chart.js packages aren't installed yet
const SignalChart = React.lazy(() => import("./SignalChart"));

const SignalViewerPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const studyId = urlParams.get('study_id');
    const patientIdsParam = urlParams.get('patient_ids');
    const signalDataParam = urlParams.get('signal_data');

    const [studyName, setStudyName] = useState("");
    const [labels, setLabels] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);

    const [signalRecords, setSignalRecords] = useState([]);
    const [selectedRecordIds, setSelectedRecordIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const initialPatientIndex = parseInt(urlParams.get('current_index') || '0', 10);
    const [currentPatientIndex, setCurrentPatientIndex] = useState(initialPatientIndex);

    // Signal annotations (time-range regions)
    const [signalAnnotations, setSignalAnnotations] = useState([]);
    // Track which annotation file IDs are currently loaded on the timeline
    const [loadedAnnotationIds, setLoadedAnnotationIds] = useState([]);

    // Saved view state
    const [savedViewConfig, setSavedViewConfig] = useState(null);
    const [isStudyOwner, setIsStudyOwner] = useState(false);
    const chartRef = useRef(null);

    // Metadata editor state
    const [showMetadataModal, setShowMetadataModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [metadataForm, setMetadataForm] = useState({
        samplingFrequency: '',
        startRecordingTime: '',
        channelNames: {},
    });


    const patientIds = useMemo(() => {
        if (patientIdsParam) return patientIdsParam.split(',');
        return [];
    }, [patientIdsParam]);

    const currentPatientId = patientIds[currentPatientIndex] || patientIds[0] || null;

    // Filter records for the current patient only (when multiple patients)
    const currentPatientRecords = useMemo(() => {
        if (patientIds.length <= 1) return signalRecords;
        return signalRecords.filter(r => r.patient_id === currentPatientId);
    }, [signalRecords, currentPatientId, patientIds.length]);

    // Handle loading JSON event annotations onto signal timeline
    const handleLoadSignalAnnotations = useCallback(async (annotationIds) => {
        if (!annotationIds || annotationIds.length === 0) return;

        try {
            const allRegions = [];

            for (const annId of annotationIds) {
                const response = await API.get(`/data_manager/patient-data/${annId}/content/`);
                const content = response.data.content;
                let parsed;
                try {
                    parsed = typeof content === 'string' ? JSON.parse(content) : content;
                } catch (e) {
                    console.warn(`Could not parse annotation ${annId} as JSON:`, e);
                    continue;
                }

                const events = parsed.annotations || parsed.events || [];
                if (events.length === 0) continue;

                // Get the earliest start_recording_time from loaded signals
                const signalStartTimes = currentPatientRecords
                    .filter(r => r.metadata?.start_datetime || r.metadata?.start_recording_time)
                    .map(r => new Date(r.metadata.start_datetime || r.metadata.start_recording_time).getTime());

                const earliestStart = signalStartTimes.length > 0
                    ? Math.min(...signalStartTimes)
                    : null;

                for (const evt of events) {
                    const label = evt.event || evt.label || 'Unknown';
                    const startAbs = new Date(evt.start_time || evt.start).getTime();
                    const stopAbs = new Date(evt.stop_time || evt.stop || evt.end_time || evt.end).getTime();

                    if (isNaN(startAbs) || isNaN(stopAbs)) continue;

                    const startSec = earliestStart ? (startAbs - earliestStart) / 1000 : startAbs / 1000;
                    const endSec = earliestStart ? (stopAbs - earliestStart) / 1000 : stopAbs / 1000;

                    allRegions.push({
                        start_seconds: Math.round(startSec * 100) / 100,
                        end_seconds: Math.round(endSec * 100) / 100,
                        label_name: label,
                        label_id: label.toLowerCase().replace(/\s+/g, '_'),
                        color: label === 'Rest' ? '#4CAF50' : label === 'Hypnosis' ? '#FF5722' : '#2196F3',
                    });
                }
            }

            if (allRegions.length > 0) {
                setSignalAnnotations(prev => [...prev, ...allRegions]);
                setLoadedAnnotationIds(prev => [...new Set([...prev, ...annotationIds])]);
                toast.success(`Loaded ${allRegions.length} event annotations`);
            } else {
                toast.warning("No event annotations found in selected files");
            }
        } catch (err) {
            console.error("Error loading signal annotations:", err);
            toast.error(err.response?.data?.error || "Failed to load annotations");
        }
    }, [currentPatientRecords]);

    // Handle patient navigation
    const handlePatientChange = useCallback((newIndex) => {
        if (newIndex < 0 || newIndex >= patientIds.length) return;
        setCurrentPatientIndex(newIndex);
        // Auto-select all records for the new patient
        const newPatientId = patientIds[newIndex];
        const newPatientRecordIds = signalRecords
            .filter(r => r.patient_id === newPatientId && !r.error)
            .map(r => r.id);
        setSelectedRecordIds(newPatientRecordIds);
    }, [patientIds, signalRecords]);

    // Delete a derived signal record
    const handleDeleteRecord = useCallback(async (recordId) => {
        if (!window.confirm('Delete this derived signal? This cannot be undone.')) return;
        try {
            await API.delete(`/data_manager/patient-data/${recordId}/`);
            setSignalRecords(prev => prev.filter(r => r.id !== recordId));
            setSelectedRecordIds(prev => prev.filter(id => id !== recordId));
            toast.success('Signal deleted');
        } catch (err) {
            console.error('Error deleting signal:', err);
            toast.error(err.response?.data?.detail || 'Failed to delete signal');
        }
    }, []);

    // Fetch study info
    const fetchStudyInfo = useCallback(async () => {
        if (!studyId) return;
        try {
            // Ensure auth token is available (page may have opened in a new tab)
            await initializeAuth();
            const response = await API.get(`/studies/${studyId}/`);
            setStudyName(response.data.name);
            setLabels(response.data.labels || []);
            setQuestions(response.data.questions || []);
            setScores(response.data.scores || []);
            setShareAnnotations(response.data.share_annotations || false);

            // Check ownership
            const currentUsername = localStorage.getItem('username');
            const isOwner = response.data.created_by_username === currentUsername
                || response.data.user_assignment?.role === 'lead';
            setIsStudyOwner(isOwner);

            // Fetch saved view config
            try {
                const viewResponse = await API.get(`/studies/${studyId}/signal_view/`);
                if (viewResponse.data && Object.keys(viewResponse.data).length > 0) {
                    setSavedViewConfig(viewResponse.data);
                }
            } catch (viewErr) {
                console.warn("No saved view config:", viewErr);
            }
        } catch (err) {
            console.error("[SignalViewerPage] Error fetching study info:", err);
        }
    }, [studyId]);

    // Fetch signal metadata for each record
    const fetchSignalData = useCallback(async () => {
        if (!signalDataParam) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const items = signalDataParam.split(',').map(item => {
                const [id, sourceModel] = item.split(':');
                return { id, sourceModel: sourceModel || 'PatientData' };
            });
            console.log('[SignalViewer] Parsed signal data items:', items);

            const records = [];
            for (const item of items) {
                try {
                    // Fetch basic info
                    console.log(`[SignalViewer] Fetching patient-data/${item.id}/...`);
                    const infoResponse = await API.get(`/data_manager/patient-data/${item.id}/`);
                    const data = infoResponse.data;
                    console.log(`[SignalViewer] PatientData info:`, data);

                    // Fetch signal metadata
                    console.log(`[SignalViewer] Fetching signal-content metadata for ${item.id}...`);
                    const metaResponse = await API.get(`/data_manager/patient-data/${item.id}/signal-content/?metadata_only=true`);
                    const metadata = metaResponse.data.metadata;
                    console.log(`[SignalViewer] Signal metadata:`, metadata);

                    records.push({
                        id: item.id,
                        patient_id: data.patient_id,
                        filename: data.filename,
                        description: data.description,
                        metadata: metadata,
                        source_execution: data.source_execution || null,
                    });
                } catch (err) {
                    console.error(`[SignalViewer] Error fetching signal record ${item.id}:`, err);
                    if (err.response) {
                        console.error(`[SignalViewer] Response status: ${err.response.status}`, err.response.data);
                    }
                    // Still add record with error state so user can access metadata editor
                    try {
                        const infoResponse = await API.get(`/data_manager/patient-data/${item.id}/`);
                        const data = infoResponse.data;
                        records.push({
                            id: item.id,
                            patient_id: data.patient_id,
                            filename: data.filename,
                            description: data.description,
                            metadata: null,
                            source_execution: data.source_execution || null,
                            error: err.response?.data?.error || 'Failed to load signal metadata',
                            needsConfiguration: err.response?.status === 400 && err.response?.data?.error?.includes('sampling_frequency')
                        });
                    } catch (infoErr) {
                        console.error(`[SignalViewer] Failed to fetch basic info for ${item.id}:`, infoErr);
                    }
                }
            }

            // Auto-discover derived (tool-generated) signals for each patient
            if (studyId) {
                const loadedIds = new Set(records.map(r => String(r.id)));
                const uniquePatients = [...new Set(records.map(r => r.patient_id).filter(Boolean))];
                // Also include patients from URL that may not have had records yet
                for (const pid of patientIds) {
                    if (!uniquePatients.includes(pid)) uniquePatients.push(pid);
                }

                for (const pid of uniquePatients) {
                    try {
                        const derivedRes = await API.get(`/data_manager/patient-data/derived/?patient_id=${pid}&study_id=${studyId}`);
                        for (const derived of derivedRes.data) {
                            if (loadedIds.has(String(derived.id))) continue;
                            loadedIds.add(String(derived.id));
                            // Fetch signal metadata for this derived record
                            try {
                                const metaRes = await API.get(`/data_manager/patient-data/${derived.id}/signal-content/?metadata_only=true`);
                                records.push({
                                    id: derived.id,
                                    patient_id: derived.patient_id,
                                    filename: derived.filename,
                                    description: derived.description,
                                    metadata: metaRes.data.metadata,
                                    source_execution: derived.source_execution,
                                });
                            } catch (metaErr) {
                                records.push({
                                    id: derived.id,
                                    patient_id: derived.patient_id,
                                    filename: derived.filename,
                                    description: derived.description,
                                    metadata: derived.metadata || null,
                                    source_execution: derived.source_execution,
                                    error: 'Failed to load signal metadata',
                                });
                            }
                        }
                    } catch (derivedErr) {
                        console.warn(`Could not fetch derived signals for patient ${pid}:`, derivedErr);
                    }
                }
            }

            setSignalRecords(records);
            if (records.length > 0) {
                // Auto-select records for the current patient only
                const firstPatient = patientIds[currentPatientIndex] || patientIds[0];
                if (patientIds.length > 1 && firstPatient) {
                    const patientRecordIds = records
                        .filter(r => r.patient_id === firstPatient && !r.error)
                        .map(r => r.id);
                    setSelectedRecordIds(patientRecordIds);
                } else {
                    setSelectedRecordIds(records.filter(r => !r.error).map(r => r.id));
                }
            }
        } catch (err) {
            console.error("Error fetching signal data:", err);
            toast.error("Failed to load signal data");
        } finally {
            setLoading(false);
        }
    }, [signalDataParam, studyId, patientIds]);

    // Load existing annotations for the current patient
    const loadAnnotations = useCallback(async () => {
        if (!studyId || !currentPatientId) return;
        try {
            const response = await API.get(`/studies/${studyId}/annotations/`);
            const annotations = response.data;
            const patientAnnotation = annotations.find(
                a => a.patient_id === currentPatientId && a.annotated_by === undefined
            ) || annotations.find(a => a.patient_id === currentPatientId);

            if (patientAnnotation?.question_answers?._signal_regions) {
                setSignalAnnotations(patientAnnotation.question_answers._signal_regions);
            } else {
                setSignalAnnotations([]);
            }
        } catch (err) {
            console.error("Error loading annotations:", err);
        }
    }, [studyId, currentPatientId]);

    // Save signal annotations
    const saveSignalAnnotations = useCallback(async (regions) => {
        if (!studyId || !currentPatientId) return;
        try {
            await API.post(`/studies/${studyId}/annotate/`, {
                patient_id: currentPatientId,
                question_answers: { _signal_regions: regions },
            });
        } catch (err) {
            console.error("Error saving signal annotations:", err);
            toast.error("Failed to save annotation");
        }
    }, [studyId, currentPatientId]);

    const handleAnnotationCreate = useCallback((startSeconds, endSeconds, label) => {
        const newRegion = {
            start_seconds: Math.round(startSeconds * 100) / 100,
            end_seconds: Math.round(endSeconds * 100) / 100,
            label_id: label.id,
            label_name: label.name,
            color: label.color || '#0d6efd',
        };
        const updated = [...signalAnnotations, newRegion];
        setSignalAnnotations(updated);
        saveSignalAnnotations(updated);
    }, [signalAnnotations, saveSignalAnnotations]);

    const handleAnnotationDelete = useCallback((index) => {
        const updated = signalAnnotations.filter((_, i) => i !== index);
        setSignalAnnotations(updated);
        saveSignalAnnotations(updated);
    }, [signalAnnotations, saveSignalAnnotations]);

    const handleAnnotationUpdate = useCallback((index, newStart, newEnd) => {
        const updated = signalAnnotations.map((ann, i) =>
            i === index
                ? { ...ann, start_seconds: Math.round(newStart * 100) / 100, end_seconds: Math.round(newEnd * 100) / 100 }
                : ann
        );
        setSignalAnnotations(updated);
        saveSignalAnnotations(updated);
    }, [signalAnnotations, saveSignalAnnotations]);

    // Apply saved view config once records are loaded
    const savedViewApplied = useRef(false);
    useEffect(() => {
        if (savedViewConfig && signalRecords.length > 0 && !savedViewApplied.current) {
            savedViewApplied.current = true;
            // Apply saved record selection
            if (savedViewConfig.selected_record_ids) {
                const validIds = savedViewConfig.selected_record_ids
                    .filter(id => signalRecords.some(r => r.id === id));
                if (validIds.length > 0) {
                    setSelectedRecordIds(validIds);
                }
            }
            // Restore signal annotations (drawn regions) from saved view
            // These already include regions from loaded annotation files,
            // so we don't need to re-load the files
            if (savedViewConfig.signal_annotations?.length > 0) {
                setSignalAnnotations(savedViewConfig.signal_annotations);
                if (savedViewConfig.loaded_annotation_ids?.length > 0) {
                    setLoadedAnnotationIds(savedViewConfig.loaded_annotation_ids);
                }
            } else if (savedViewConfig.loaded_annotation_ids?.length > 0) {
                // No saved annotations but has file IDs — load from files
                setLoadedAnnotationIds(savedViewConfig.loaded_annotation_ids);
                handleLoadSignalAnnotations(savedViewConfig.loaded_annotation_ids);
            }
        }
    }, [savedViewConfig, signalRecords, handleLoadSignalAnnotations]);

    // Save current view (owner/admin only)
    const handleSaveView = useCallback(async () => {
        const chartState = chartRef.current?.getViewState() || {};
        const config = {
            selected_record_ids: selectedRecordIds,
            selected_channels: chartState.selectedChannels || {},
            zoom_range: {
                start_seconds: chartState.windowStart || 0,
                window_duration: chartState.windowDuration || 10,
            },
            time_alignment: chartState.timeAlignment || 'relative',
            loaded_annotation_ids: loadedAnnotationIds,
            signal_annotations: signalAnnotations,
        };
        try {
            await API.put(`/studies/${studyId}/save_signal_view/`, { config });
            toast.success('View saved for all study members');
        } catch (err) {
            console.error("Error saving view:", err);
            toast.error('Failed to save view');
        }
    }, [studyId, selectedRecordIds, loadedAnnotationIds, signalAnnotations]);

    // Open metadata editor
    const handleEditMetadata = useCallback((record) => {
        setEditingRecord(record);
        // Pre-fill form with existing values from metadata
        // Check user-provided first, then schema-provided, then empty
        const existingFreq = record.metadata?.user_provided_sampling_frequency
            ? (record.metadata.channels?.[0]?.sample_rate || '')
            : (record.metadata?.sampling_frequency || '');
        const existingStartTime = record.metadata?.start_datetime
            || record.metadata?.start_recording_time
            || '';

        // Convert ISO datetime to datetime-local format with seconds (YYYY-MM-DDTHH:mm:ss)
        let formattedStartTime = '';
        if (existingStartTime) {
            try {
                // Include seconds for precise alignment
                formattedStartTime = existingStartTime.slice(0, 19);
            } catch (e) {
                console.warn('Could not parse start_datetime:', existingStartTime);
            }
        }

        // Pre-fill custom channel names
        const channelNames = {};
        if (record.metadata?.channels) {
            record.metadata.channels.forEach((ch) => {
                channelNames[ch.index] = ch.label || '';
            });
        }

        setMetadataForm({
            samplingFrequency: existingFreq,
            startRecordingTime: formattedStartTime,
            channelNames,
        });
        setShowMetadataModal(true);
    }, []);

    // Save metadata
    const handleSaveMetadata = useCallback(async () => {
        if (!editingRecord) return;

        try {
            const payload = {};
            if (metadataForm.samplingFrequency) {
                payload.sampling_frequency = parseFloat(metadataForm.samplingFrequency);
            }
            if (metadataForm.startRecordingTime) {
                payload.start_recording_time = metadataForm.startRecordingTime;
            }
            if (metadataForm.channelNames && Object.keys(metadataForm.channelNames).length > 0) {
                payload.channel_names = metadataForm.channelNames;
            }

            await API.patch(`/data_manager/patient-data/${editingRecord.id}/signal-metadata/`, payload);
            toast.success('Signal metadata updated successfully');

            // Refresh signal data to get updated duration
            await fetchSignalData();
            setShowMetadataModal(false);
        } catch (err) {
            console.error('[SignalViewer] Error saving metadata:', err);
            toast.error(`Failed to save metadata: ${err.response?.data?.error || err.message}`);
        }
    }, [editingRecord, metadataForm, fetchSignalData]);

    useEffect(() => {
        fetchStudyInfo();
    }, [fetchStudyInfo]);

    useEffect(() => {
        fetchSignalData();
    }, [fetchSignalData]);

    useEffect(() => {
        loadAnnotations();
    }, [loadAnnotations]);

    const formatDuration = (seconds) => {
        if (!seconds) return "0s";
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        if (h > 0) return `${h}h ${m}m ${s}s`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    return (
        <div className="dv-viewer-page signal-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                questions={questions}
                scores={scores}
                patientId={currentPatientId}
                shareAnnotations={shareAnnotations}
                showTools={true}
                signalRecords={currentPatientRecords}
                selectedSignalIds={selectedRecordIds}
                patientIds={patientIds}
                currentPatientIndex={currentPatientIndex}
                onPatientChange={handlePatientChange}
                onLoadSignalAnnotations={handleLoadSignalAnnotations}
            />

            <div className="dv-viewer-content">
                <div className="signal-page-layout">
                    {/* Left Sidebar - Record List */}
                    <div className="signal-sidebar">
                        <div className="signal-sidebar-header">
                            <i className="fa fa-wave-square me-2"></i>
                            <span>Signal Records</span>
                            <span className="badge bg-info ms-2">{currentPatientRecords.length}</span>
                        </div>
                        <div className="signal-record-list">
                            {loading ? (
                                <div className="signal-loading">
                                    <div className="spinner-border spinner-border-sm text-info" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span className="ms-2">Loading records...</span>
                                </div>
                            ) : currentPatientRecords.length === 0 ? (
                                <div className="signal-empty-sidebar">
                                    <i className="fa fa-wave-square"></i>
                                    <p>No signal records found for this patient</p>
                                </div>
                            ) : (
                                currentPatientRecords.map(record => {
                                    const isSelected = selectedRecordIds.includes(record.id);
                                    const hasError = record.error != null;
                                    const isDerived = !!record.source_execution;
                                    return (
                                        <div
                                            key={record.id}
                                            className={`signal-record-item ${isSelected ? 'active' : ''} ${hasError ? 'error' : ''}`}
                                            onClick={() => {
                                                // Don't allow selection if record has error
                                                if (hasError) return;
                                                setSelectedRecordIds(prev =>
                                                    prev.includes(record.id)
                                                        ? prev.filter(id => id !== record.id)
                                                        : [...prev, record.id]
                                                );
                                            }}
                                            style={hasError ? { cursor: 'default', opacity: 0.8 } : {}}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={hasError}
                                                onChange={() => {}}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ marginRight: '0.5rem', accentColor: isDerived ? '#fd7e14' : '#17a2b8' }}
                                            />
                                            <div className="signal-record-icon">
                                                <i className={`fa ${hasError ? 'fa-exclamation-triangle text-warning' : isDerived ? 'fa-flask' : 'fa-wave-square'}`}
                                                   style={isDerived ? { color: '#fd7e14' } : {}}></i>
                                            </div>
                                            <div className="signal-record-info">
                                                <div className="signal-record-name">
                                                    {record.filename || `Signal ${record.id}`}
                                                </div>
                                                <div className="signal-record-meta">
                                                    <span><i className="fa fa-user me-1"></i>{record.patient_id}</span>
                                                    {hasError ? (
                                                        <span style={{ color: '#ffc107', fontSize: '0.85rem' }}>
                                                            <i className="fa fa-exclamation-circle me-1"></i>
                                                            {record.needsConfiguration ? 'Needs configuration' : 'Error loading'}
                                                        </span>
                                                    ) : record.metadata ? (
                                                        <>
                                                            <span><i className="fa fa-clock me-1"></i>{formatDuration(record.metadata.duration_seconds)}</span>
                                                            <span><i className="fa fa-bars me-1"></i>{record.metadata.num_channels} ch</span>
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem' }}>
                                                {isDerived && (
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteRecord(record.id);
                                                        }}
                                                        title="Delete derived signal"
                                                        style={{ padding: '0.25rem 0.5rem' }}
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                )}
                                                <button
                                                    className={`btn btn-sm ${hasError ? 'btn-warning' : 'btn-outline-secondary'}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditMetadata(record);
                                                    }}
                                                    title={hasError ? 'Configure signal metadata' : 'Edit signal metadata'}
                                                    style={{ padding: '0.25rem 0.5rem' }}
                                                >
                                                    <i className={`fa ${hasError ? 'fa-wrench' : 'fa-cog'}`}></i>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Main Content - Signal Chart */}
                    <div className="signal-main-content">
                        {selectedRecordIds.length === 0 ? (
                            <div className="signal-empty-state">
                                <i className="fa fa-wave-square"></i>
                                <h3>No Signal Selected</h3>
                                <p>Select signal records from the sidebar to view their waveforms.</p>
                            </div>
                        ) : (
                            <Suspense fallback={
                                <div className="signal-empty-state">
                                    <div className="spinner-border text-info" role="status">
                                        <span className="visually-hidden">Loading chart...</span>
                                    </div>
                                    <p className="mt-3">Loading signal chart...</p>
                                </div>
                            }>
                                <SignalChart
                                    ref={chartRef}
                                    allRecords={currentPatientRecords}
                                    records={currentPatientRecords.filter(r => selectedRecordIds.includes(r.id))}
                                    annotations={signalAnnotations}
                                    labels={labels}
                                    onAnnotationCreate={handleAnnotationCreate}
                                    onAnnotationDelete={handleAnnotationDelete}
                                    onAnnotationUpdate={handleAnnotationUpdate}
                                    initialViewConfig={savedViewConfig}
                                    isStudyOwner={isStudyOwner}
                                    onSaveView={handleSaveView}
                                />
                            </Suspense>
                        )}
                    </div>
                </div>
            </div>

            {/* Signal Metadata Editor Modal */}
            {showMetadataModal && editingRecord && (
                <div className="modal-backdrop" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                }}>
                    <div className="modal-dialog" style={{ maxWidth: '500px', width: '90%' }}>
                        <div className="modal-content" style={{ background: '#1c2128', border: '1px solid #30363d', borderRadius: '12px' }}>
                            <div className="modal-header" style={{ borderBottom: '1px solid #30363d', padding: '1rem 1.5rem' }}>
                                <h5 className="modal-title" style={{ color: '#58a6ff', margin: 0, fontWeight: 600 }}>
                                    <i className="fa fa-cog me-2"></i>
                                    Edit Signal Metadata
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setShowMetadataModal(false)}
                                    style={{ background: 'transparent', border: 'none', color: '#8b949e', fontSize: '1.5rem', cursor: 'pointer' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>
                            <div className="modal-body" style={{ padding: '1.5rem' }}>
                                <div style={{ marginBottom: '1rem' }}>
                                    <p style={{ color: '#8b949e', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                        <strong style={{ color: '#e6edf3' }}>{editingRecord.filename}</strong>
                                    </p>
                                    {editingRecord.error ? (
                                        <div style={{
                                            padding: '0.75rem 1rem',
                                            background: 'rgba(255, 193, 7, 0.1)',
                                            border: '1px solid rgba(255, 193, 7, 0.3)',
                                            borderRadius: '6px',
                                            marginBottom: '1rem'
                                        }}>
                                            <p style={{ color: '#ffc107', fontSize: '0.85rem', margin: 0 }}>
                                                <i className="fa fa-exclamation-triangle me-2"></i>
                                                <strong>Configuration Required:</strong>
                                            </p>
                                            <p style={{ color: '#8b949e', fontSize: '0.8rem', margin: '0.5rem 0 0 0' }}>
                                                {editingRecord.error}
                                            </p>
                                        </div>
                                    ) : (
                                        <p style={{ color: '#8b949e', fontSize: '0.85rem' }}>
                                            Current duration: <strong>{formatDuration(editingRecord.metadata?.duration_seconds)}</strong>
                                            {editingRecord.metadata?.user_provided_sampling_frequency && (
                                                <span style={{ color: '#17a2b8', marginLeft: '0.5rem' }}>
                                                    <i className="fa fa-check-circle me-1"></i>
                                                    Using custom frequency
                                                </span>
                                            )}
                                            {editingRecord.metadata?.user_provided_start_time && (
                                                <span style={{ color: '#17a2b8', marginLeft: '0.5rem' }}>
                                                    <i className="fa fa-clock me-1"></i>
                                                    Start time set
                                                </span>
                                            )}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                                        Sampling Frequency (Hz)
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={metadataForm.samplingFrequency}
                                        onChange={(e) => setMetadataForm(prev => ({ ...prev, samplingFrequency: e.target.value }))}
                                        placeholder="e.g., 100, 250, 1000"
                                        style={{
                                            background: '#21262d',
                                            border: '1px solid #30363d',
                                            color: '#e6edf3',
                                            padding: '0.5rem',
                                            borderRadius: '6px'
                                        }}
                                    />
                                    <small style={{ color: '#6e7681', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                        Enter the correct sampling rate if the auto-detected value is wrong
                                    </small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                                        Start Recording Time (Optional)
                                    </label>
                                    <input
                                        type="datetime-local"
                                        step="1"
                                        className="form-control"
                                        value={metadataForm.startRecordingTime}
                                        onChange={(e) => setMetadataForm(prev => ({ ...prev, startRecordingTime: e.target.value }))}
                                        style={{
                                            background: '#21262d',
                                            border: '1px solid #30363d',
                                            color: '#e6edf3',
                                            padding: '0.5rem',
                                            borderRadius: '6px'
                                        }}
                                    />
                                    <small style={{ color: '#6e7681', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                        Specify when this recording started (date, time, and seconds)
                                    </small>
                                </div>

                                {editingRecord?.metadata?.channels?.length > 0 && (
                                    <div className="mb-3">
                                        <label className="form-label" style={{ color: '#e6edf3', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                                            Channel Names
                                        </label>
                                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {editingRecord.metadata.channels.map((ch) => (
                                                <div key={ch.index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                                                    <span style={{ color: '#8b949e', fontSize: '0.8rem', minWidth: '30px' }}>
                                                        {ch.index + 1}.
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={metadataForm.channelNames[ch.index] || ''}
                                                        onChange={(e) => setMetadataForm(prev => ({
                                                            ...prev,
                                                            channelNames: { ...prev.channelNames, [ch.index]: e.target.value }
                                                        }))}
                                                        style={{
                                                            background: '#21262d',
                                                            border: '1px solid #30363d',
                                                            color: '#e6edf3',
                                                            padding: '0.3rem 0.5rem',
                                                            borderRadius: '4px',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <small style={{ color: '#6e7681', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                            Rename channels displayed in the chart
                                        </small>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer" style={{ borderTop: '1px solid #30363d', padding: '1rem 1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowMetadataModal(false)}
                                    style={{ padding: '0.5rem 1rem', borderRadius: '6px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSaveMetadata}
                                    style={{ padding: '0.5rem 1rem', borderRadius: '6px', background: '#17a2b8', border: 'none' }}
                                >
                                    <i className="fa fa-save me-2"></i>
                                    Save Metadata
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar theme="dark" />
        </div>
    );
};

export default SignalViewerPage;
